import * as React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

import { FOCUS_RING } from "../lib/focus";
import { HIT_AREA } from "../lib/target";
import { cn } from "../lib/utils";
import { useFieldControl } from "./field";

/**
 * v2 Switch (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap.
 *
 * One size, 32×18, thumb 14 (break #55). Off: `--input` track; on: `--primary`;
 * thumb white in both modes, no shadow. Hover is a 4px `--hover-overlay` halo
 * in both states: a darker or lighter off track would drop the white thumb
 * below 3:1 in one of the modes. The thumb slides in 150ms, not at all under
 * reduced motion. The hit area is 24px tall through a pseudo element (`HIT_AREA`).
 *
 * Settings rows put it in `<Field orientation="horizontal" controlWidth="fit">`.
 *
 * State is mirrored and handed to Radix as controlled, like Checkbox, so the
 * classes are plain conditionals. `data-hover` / `data-focus` are for stories.
 */

export type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>;

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(function Switch(
  props,
  ref,
) {
  const { checked, defaultChecked, onCheckedChange, className, ...rest } = useFieldControl(props);
  const [uncontrolled, setUncontrolled] = React.useState(defaultChecked ?? false);
  const controlled = checked !== undefined;
  const on = controlled ? checked : uncontrolled;

  return (
    // Radix renders its hidden form input (inside a <form> only) as an absolute SIBLING after the
    // root, not inside it, so the root's own `relative` can't hold it. Without a positioned box
    // around both, the input's containing block is the page: it escapes a scrolling pane and
    // stretches the document (break #180).
    <span data-slot="switch-box" className="relative inline-flex shrink-0 align-middle">
      <SwitchPrimitive.Root
        ref={ref}
        checked={on}
        onCheckedChange={(next) => {
          if (!controlled) setUncontrolled(next);
          onCheckedChange?.(next);
        }}
        className={cn(
          "relative inline-flex h-4.5 w-8 shrink-0 cursor-pointer items-center rounded-full px-0.5 transition duration-150 motion-reduce:transition-none",
          // 32×24 hit area around the 32×18 track.
          HIT_AREA,
          "ring-hover-overlay enabled:hover:ring-4 data-hover:ring-4",
          FOCUS_RING,
          "disabled:cursor-not-allowed disabled:opacity-50",
          on ? "bg-primary" : "bg-input",
          className,
        )}
        {...rest}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            "pointer-events-none block size-3.5 rounded-full bg-primary-foreground transition-transform duration-150 motion-reduce:transition-none",
            on ? "translate-x-3.5" : "translate-x-0",
          )}
        />
      </SwitchPrimitive.Root>
    </span>
  );
});

export { Switch };
